import { notifyOnError } from '~/rage/decorators';
import { IObservable, SeenTracker, Subject } from '~/rage/utils';
import {
	IClientConfigs,
	IVehicleConsumable,
	VehicleConsumableGroup,
} from '~/shared';
import { BackendService } from '../BackendService';
import { GameConfigService } from '../GameConfigService';
import { RpcService } from '../RpcService';
import { VarsService } from '../VarsService';
import { VehicleService } from '../VehicleService';
import { IVehicleServiceStationService } from './vehicleServiceStationServiceTypes';

export class VehicleServiceStationService
	implements IVehicleServiceStationService
{
	private readonly consumables = new Subject<
		IClientConfigs['vehicleWear']['consumables']
	>([]);
	private seenTracker = new SeenTracker('vehicle_service');
	private readonly consumablesHealth = new Subject<
		Partial<Record<VehicleConsumableGroup, number>>
	>({});

	private readonly engineRepairPrice = new Subject(0);

	constructor(
		private readonly rpc: RpcService,
		private readonly vars: VarsService,
		private readonly backend: BackendService,
		private readonly vehicles: VehicleService,
		private readonly gameConfig: GameConfigService,
	) {
		this.vars
			.get<IVehicleConsumable[]>('vehicleServiceStation:consumablesHealth', [])
			.subscribe(this.handleConsumablesUpdate.bind(this));

		gameConfig.getConfigs().subscribe(this.handleGameConfigChange.bind(this));
	}

	private async handleConsumablesUpdate(
		consumables: IVehicleConsumable[],
	): Promise<void> {
		await this.gameConfig.waitForInit();

		const consumablesHealth: Partial<Record<VehicleConsumableGroup, number>> =
			{};
		for (const { group, healthPercent } of consumables) {
			consumablesHealth[group] = healthPercent;
		}

		this.consumablesHealth.next(consumablesHealth);
	}

	private handleGameConfigChange(configs: Partial<IClientConfigs>): void {
		if (!configs || !configs.vehicleWear) return;

		this.consumables.next(configs.vehicleWear.consumables);
		this.engineRepairPrice.next(configs.vehicleWear.engineRepairPrice);
	}

	public getConsumables(): IObservable<
		IClientConfigs['vehicleWear']['consumables']
	> {
		return this.consumables;
	}

	public getEngineHealth(): IObservable<number> {
		return this.vars.get('vehicleServiceStation:engineHealth', 100);
	}

	public getConsumablesHealth(): IObservable<
		Partial<Record<VehicleConsumableGroup, number>>
	> {
		return this.consumablesHealth;
	}

	public getEngineRepairPrice(): IObservable<number> {
		return this.engineRepairPrice;
	}

	@notifyOnError
	public async buyConsumable(consumableId: string): Promise<void> {
		const vehicleId = this.vehicles.getLocalOwnedVehicleId().getValue();
		await this.rpc.invokeServer('vehicleServiceStation:buyConsumable', {
			consumableId,
			vehicleId,
		});
	}

	@notifyOnError
	public async repairEngine(): Promise<void> {
		const vehicleId = this.vehicles.getLocalOwnedVehicleId().getValue();
		await this.backend.invoke('vehicleServiceStation:repairEngine', {
			vehicleId,
		});
	}

	public isUnseen(path: string): boolean {
		return this.seenTracker.isUnseen(path);
	}

	public markSeen(path: string): void {
		this.seenTracker.markAsSeen(path);
	}

	public async exitMenu(): Promise<void> {
		await this.rpc.invokeClient('vehicleServiceStation:exitMenu');
	}
}
